import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQueryClient } from '@tanstack/react-query'
import { Droplets, Zap, Plus } from 'lucide-react'
import { Modal, Button } from '@/components/ui'
import { Spinner } from '@/components/ui/Spinner'
import { usePropertyUnits } from '@/hooks/useUnits'
import { useCreateMeter } from '@/hooks/useMeters'
import { useNotificationStore } from '@/stores/useNotificationStore'

export interface AddMeterModalProps {
  propertyId: number
  meterType: 'water' | 'electricity'
  /** unit_ids that already have a meter of `meterType` — excluded from the list. */
  existingUnitIds: number[]
  isOpen: boolean
  onClose: () => void
}

/**
 * B-057 — inline meter provisioning from the readings page.
 *
 * A unit can carry an active metered lease but have zero Meter rows (e.g. it was
 * created without ticking water/electricity, then leased on a metered formula).
 * The readings page then shows "no meters" with no way forward — a dead-end that
 * also blocks recording the reading the invoice needs. This modal lists the
 * property's units that lack a meter of the selected type and lets the user
 * create one in place; the readings table refetches so the new meter's input row
 * appears immediately.
 *
 * Apartment-only: meter writes are blocked server-side for condominium accounts
 * (the readings page itself is not reachable for condos), so no condo guard here.
 */
export function AddMeterModal({
  propertyId,
  meterType,
  existingUnitIds,
  isOpen,
  onClose,
}: AddMeterModalProps) {
  const { t: tm } = useTranslation('meters')
  const addToast = useNotificationStore((s) => s.addToast)
  const queryClient = useQueryClient()
  const createMeter = useCreateMeter()

  const { data, isLoading } = usePropertyUnits(propertyId)
  // Units created in THIS modal session — hidden optimistically so the row
  // disappears before the parent's property-readings refetch lands.
  const [addedUnitIds, setAddedUnitIds] = useState<number[]>([])
  const [pendingUnitId, setPendingUnitId] = useState<number | null>(null)

  const excluded = new Set([...existingUnitIds, ...addedUnitIds])
  const units = data?.data ?? []
  const candidates = units.filter((u) => !excluded.has(u.id))
  const hasMorePages = (data?.meta?.total_pages ?? 1) > 1

  const typeLabel = tm(`types.${meterType}`)
  const MeterIcon = meterType === 'water' ? Droplets : Zap

  const handleAdd = async (unitId: number) => {
    setPendingUnitId(unitId)
    try {
      await createMeter.mutateAsync({ unitId, meter_type: meterType })
      setAddedUnitIds((prev) => [...prev, unitId])
      // useCreateMeter only invalidates the per-unit meter list; the bulk
      // readings table reads ['property-readings'], so refresh that too.
      queryClient.invalidateQueries({ queryKey: ['property-readings'] })
      addToast({ type: 'success', message: tm('add_meter_modal.added', { type: typeLabel }) })
    } catch {
      addToast({ type: 'error', message: tm('add_meter_modal.error', { type: typeLabel }) })
    } finally {
      setPendingUnitId(null)
    }
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={tm('add_meter_modal.title', { type: typeLabel })}
    >
      {/* No footer Close button — the Modal already renders a top-right ✕. */}
      <div className="space-y-4">
        <p className="text-sm text-base-content/70">
          {tm('add_meter_modal.description', { type: typeLabel })}
        </p>

        {isLoading ? (
          <div className="py-6 text-center">
            <Spinner size="md" />
          </div>
        ) : units.length === 0 ? (
          // B-023: distinguish "this property has no units yet" from "every unit
          // already has a meter". The old code showed all_have_meters for both,
          // which read as a contradiction next to the page's "no meters found"
          // empty-state when the property simply had no units.
          <p className="py-6 text-center text-sm text-base-content/60">
            {tm('add_meter_modal.no_units')}
          </p>
        ) : candidates.length === 0 ? (
          <p className="py-6 text-center text-sm text-base-content/60">
            {tm('add_meter_modal.all_have_meters', { type: typeLabel })}
          </p>
        ) : (
          // Cap the list height so a long unit list scrolls WITHIN the sheet
          // instead of pushing the header ✕ / footer Close off a short mobile
          // viewport (B-023: "bottom sheet extends past viewport, no dismiss").
          <ul className="divide-y divide-base-200 max-h-[50vh] overflow-y-auto">
            {candidates.map((unit) => (
              <li key={unit.id} className="flex items-center justify-between gap-3 py-2">
                <span className="font-medium">{unit.name || unit.number}</span>
                <Button
                  size="sm"
                  variant="outline"
                  loading={pendingUnitId === unit.id}
                  disabled={pendingUnitId !== null}
                  onClick={() => handleAdd(unit.id)}
                  leftIcon={
                    <>
                      <Plus className="w-4 h-4" />
                      <MeterIcon className="w-4 h-4 mr-1" aria-hidden="true" />
                    </>
                  }
                >
                  {tm('add_meter_modal.add_for_type', { type: typeLabel })}
                </Button>
              </li>
            ))}
          </ul>
        )}

        {hasMorePages && (
          <p className="text-xs text-base-content/50">
            {tm('add_meter_modal.more_units_hint')}
          </p>
        )}
      </div>
    </Modal>
  )
}
